import { z } from 'zod'
import { componentIdSchema } from '@customTypes/cloudmix/sessions/components/base'
import { ComponentId } from '@customTypes/cloudmix/sessions/components'
import { useSearch, useNavigate } from '@tanstack/react-router'

export const ComponentTypes = {
  MIXER: 'mixer',
  SOURCE: 'source',
  DESTINATION: 'destination',
  CONTRIBUTOR: 'contributor',
} as const
export const componentTypeSchema = z.nativeEnum(ComponentTypes)
export type ComponentType = z.infer<typeof componentTypeSchema>

export const ComponentPanelTypes = {
  SETTINGS: 'settings',
  LINKS: 'links',
  INPUTS: 'inputs',
} as const
export const componentPanelTypeSchema = z.nativeEnum(ComponentPanelTypes)
export type ComponentPanelType = z.infer<typeof componentPanelTypeSchema>

export const routerSchema = z.object({
  ct: componentTypeSchema.optional(),
  id: componentIdSchema.optional(),
  cp: componentPanelTypeSchema.optional(),
  i: z.string().optional(),
})

export const useManagementRouter = () => {
  const search = useSearch({
    strict: false,
  })
  const navigate = useNavigate()

  const { id, ct, cp, i } = search

  const selectComponentPanel = (subPanel: ComponentPanelType) => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        i: undefined,
        cp: subPanel,
      }),
    })
  }

  const selectComponentType = (componentType: ComponentType) => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        id: undefined,
        ct: componentType,
      }),
    })
  }

  const selectComponent = (componentId: ComponentId, type: ComponentType) => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        id: componentId,
        ct: type,
      }),
    })
  }

  const deselectComponent = () => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        id: undefined,
        i: undefined,
      }),
    })
  }

  const selectComponentInput = (inputId: string) => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        i: inputId,
      }),
    })
  }

  const deselectComponentInput = () => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        i: undefined,
      }),
    })
  }

  return {
    componentId: id,
    contentType: ct,
    componentPanel: cp,
    inputId: i,
    selectComponentPanel,
    selectComponentType,
    selectComponent,
    deselectComponent,
    selectComponentInput,
    deselectComponentInput,
  }
}
